import { useEffect, useState } from "react";
import { ChartCard } from "../../ChartCard";
import { Hint } from "../Hint";
import { useRestartHistory, type RestartEvent, type RestartHistory, type RestartIncident } from "../../../../hooks/useRestartHistory";
import { FRESH_FOR_MS, eventTone, formatDuration, gatewayTelemetry, humanize, type GatewayTelemetry } from "../../../../lib/restartTelemetry";
import { formatDateTimeCompact, formatDateTimeSeconds, formatRelativeTime } from "../../../../utils/formatDateTime";
import type { InstanceRange } from "../../../../lib/instanceDetailRange";
import styles from "./GatewayRestartsPanel.module.css";

const COUNTS: ReadonlyArray<{ key: keyof RestartHistory; label: string; hint?: string; tone: "warn" | "crit" }> = [
  { key: "restartCount", label: "Restarts", tone: "warn" },
  { key: "unexpectedRestartCount", label: "Unexpected", hint: "No platform, operator or config change triggered it.", tone: "warn" },
  { key: "failedExitCount", label: "Failed exits", tone: "crit" },
  { key: "oomEventCount", label: "OOM events", hint: "Can kill a child process without restarting the gateway.", tone: "crit" },
];

const Counts = ({ data }: { data: RestartHistory }): JSX.Element => (
  <dl className={styles.counts}>
    {COUNTS.map(({ key, label, hint, tone }) => {
      const value = Number(data[key] ?? 0);
      return (
        <div key={key} className={styles.count} data-tone={value > 0 ? tone : undefined}>
          <dt>{label}{hint != null && <Hint text={hint} />}</dt>
          <dd>{value}</dd>
        </div>
      );
    })}
  </dl>
);

const signal = (value: boolean | null, yes: string, no: string): { text: string; tone: string } =>
  value == null ? { text: "unknown", tone: "muted" } : value ? { text: yes, tone: "ok" } : { text: no, tone: "crit" };

const Signals = ({ data, telemetry }: { data: RestartHistory; telemetry: GatewayTelemetry }): JSX.Element => {
  const items = [
    { label: "HTTP", ...signal(telemetry.httpHealthy, "responding", "unreachable") },
    { label: "Service", ...signal(telemetry.serviceActive, "active", "inactive") },
    { label: "Resources", ...signal(telemetry.resourceSampleAvailable, "sampled", "unavailable") },
  ];
  return (
    <ul className={styles.signals}>
      {items.map((item) => (
        <li key={item.label}>
          <span className={styles.dot} data-tone={item.tone} aria-hidden />
          {item.label} <strong>{item.text}</strong>
        </li>
      ))}
      <li title={formatDateTimeSeconds(data.lastStart)}>Started <strong>{data.lastStart ? formatRelativeTime(data.lastStart) : "unknown"}</strong></li>
      <li title={formatDateTimeSeconds(data.lastSeen)}>Telemetry <strong>{data.lastSeen ? formatRelativeTime(data.lastSeen) : "never"}</strong></li>
    </ul>
  );
};

const Notices = ({ telemetry, paused }: { telemetry: GatewayTelemetry; paused: boolean }): JSX.Element | null => {
  if (paused) return <p className={styles.notice}>Instance paused. Showing recorded history.</p>;
  if (telemetry.state !== "reporting") {
    return <p className={styles.notice}>Telemetry {telemetry.state === "stale" ? "is stale" : "is not reporting"}, so zero counts do not mean the gateway is healthy.</p>;
  }
  if (telemetry.httpHealthy === false) return <p className={styles.notice} data-tone="crit">Gateway unreachable.</p>;
  return null;
};

const Incidents = ({ incidents }: { incidents: ReadonlyArray<RestartIncident> }): JSX.Element => (
  <section className={styles.section}>
    <h4 className={styles.sectionTitle}>
      Recovery incidents <span className={styles.countTag}>{incidents.length}</span>
      <Hint text="Latest 50. Recovery needs two successful HTTP checks; channel health is tracked separately." />
    </h4>
    <div className={styles.scroll}>
      <table className={styles.table}>
        <thead><tr><th>Started</th><th className={styles.num}>Down for</th><th className={styles.num}>Restart attempts</th><th>Diagnostic</th></tr></thead>
        <tbody>
          {incidents.map((incident) => (
            <tr key={incident.incidentId}>
              <td title={formatDateTimeSeconds(incident.startedAt)}>{formatDateTimeCompact(incident.startedAt) || "unknown"}</td>
              <td className={styles.num} title={incident.recoveredAt ? `Recovered ${formatDateTimeSeconds(incident.recoveredAt)}` : undefined}>{incident.recoveredAt ? formatDuration(incident.recoverySeconds ?? 0) : <span className={styles.crit}>not recovered</span>}</td>
              <td className={styles.num}>{incident.restartAttempts}</td>
              <td className={styles.muted}>{incident.diagnosticCode ? humanize(incident.diagnosticCode) : "unknown"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const eventDetail = (event: RestartEvent): string =>
  [
    event.diagnosticCode ? humanize(event.diagnosticCode) : null,
    event.instanceType ? `${event.previousInstanceType ? `${event.previousInstanceType} -> ` : ""}${event.instanceType}` : null,
    event.resourceSnapshot?.GatewayMemoryPercent != null ? `mem ${event.resourceSnapshot.GatewayMemoryPercent.toFixed(0)}% of cap` : null,
  ].filter((part): part is string => part !== null).join(" · ");

const EventRow = ({ event }: { event: RestartEvent }): JSX.Element => (
  <tr title={`Instance ${event.instanceId}${event.operationId ? ` · operation ${event.operationId}` : ""}`}>
    <td title={formatDateTimeSeconds(event.timestamp)}>{formatDateTimeCompact(event.timestamp)}</td>
    <td><span className={styles.eventKind}><span className={styles.dot} data-tone={eventTone(event)} aria-hidden />{humanize(event.kind)}</span></td>
    <td>{humanize(event.cause)}{event.exitStatus ? ` (${event.exitStatus})` : ""}</td>
    <td>{humanize(event.trigger)}{event.unexpected ? <span className={styles.warn}> unexpected</span> : null}</td>
    <td className={styles.muted} title={event.resourceSnapshot?.sampledAt ? `Memory sampled ${formatDateTimeSeconds(event.resourceSnapshot.sampledAt)}` : undefined}>{eventDetail(event)}</td>
  </tr>
);

type EventsProps = { data: RestartHistory; page: number; busy: boolean; onPage: (next: number) => void };

const Events = ({ data, page, busy, onPage }: EventsProps): JSX.Element => (
  <details className={styles.section}>
    <summary className={styles.sectionTitle}>
      Lifecycle events <span className={styles.countTag}>{data.events.length}{data.nextPage !== null ? "+" : ""}</span>
    </summary>
    {data.events.length === 0 ? <p className={styles.muted}>No lifecycle events in this window.</p> : (
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead><tr><th>Time</th><th>Event</th><th>Outcome</th><th>Trigger</th><th>Detail</th></tr></thead>
          <tbody>{data.events.map((event) => <EventRow key={event.eventId} event={event} />)}</tbody>
        </table>
      </div>
    )}
    {(page > 0 || data.nextPage !== null) && (
      <div className={styles.pager}>
        <button type="button" disabled={page === 0 || busy} onClick={() => onPage(page - 1)}>Previous</button>
        <span>Page {page + 1}</span>
        <button type="button" disabled={data.nextPage === null || busy} onClick={() => onPage(data.nextPage ?? page)}>Next</button>
      </div>
    )}
  </details>
);

const cloudWatchUrl = (tenantId: string): string =>
  `https://ap-southeast-2.console.aws.amazon.com/cloudwatch/home?region=ap-southeast-2#dashboards/dashboard/ztc-tenant-detail?var.tenant=${encodeURIComponent(tenantId)}`;

// Re-render once the telemetry crosses the freshness line, so the live
// signals flip to unknown without waiting for the next fetch.
const useStaleTick = (lastSeen: string | null | undefined): void => {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!lastSeen) return;
    const expires = Date.parse(lastSeen) + FRESH_FOR_MS + 1;
    if (!Number.isFinite(expires)) return;
    const timer = window.setTimeout(() => tick((v) => v + 1), Math.max(0, expires - Date.now()));
    return () => window.clearTimeout(timer);
  }, [lastSeen]);
};

type PanelProps = { tenantId: string; range: InstanceRange; paused: boolean };

const RestartHistoryBody = ({ tenantId, range, paused }: PanelProps): JSX.Element => {
  const [page, setPage] = useState(0);
  const { data, status, error, isFetching } = useRestartHistory(tenantId, range, page);
  useStaleTick(data?.lastSeen);
  const telemetry = data ? gatewayTelemetry(data, Date.now()) : null;
  return (
    <ChartCard
      title="Gateway"
      subtitle={data?.partial ? `History since ${data.coverageStart ? formatDateTimeCompact(data.coverageStart) : "collection began"}; earlier gaps possible` : undefined}
      right={<a className={styles.external} href={cloudWatchUrl(tenantId)} target="_blank" rel="noreferrer">CloudWatch ↗</a>}
      wide
      state={status === "loading" ? "loading" : status === "error" ? "error" : "ready"}
      errorText={error ?? undefined}
    >
      {data && telemetry && (
        <div className={styles.body} aria-busy={isFetching}>
          <Counts data={data} />
          <Signals data={data} telemetry={telemetry} />
          <Notices telemetry={telemetry} paused={paused} />
          {!!data.incidents?.length && <Incidents incidents={data.incidents} />}
          <Events data={data} page={page} busy={isFetching} onPage={setPage} />
        </div>
      )}
    </ChartCard>
  );
};

export const GatewayRestartsPanel = (props: PanelProps): JSX.Element =>
  <RestartHistoryBody key={`${props.tenantId}:${props.range}`} {...props} />;
